import { useState, useCallback } from 'react';

interface UsePhotoItemProps {
  photoId: string;
  originalBlobName: string;
  onUpdateCaption?: (photoId: string, caption: string) => void;
  onDelete: (photoId: string) => Promise<void>;
  onCaptionChange?: (photoId: string, caption: string) => void;
}

export const usePhotoItem = ({ photoId, originalBlobName, onUpdateCaption, onDelete, onCaptionChange }: UsePhotoItemProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const [isLoadingOriginal, setIsLoadingOriginal] = useState(false);

  const handleEditClick = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handlePhotoClick = useCallback(async () => {
    if (isEditing) return; // Don't open full screen if editing

    try {
      // The FullScreenImageDialog will handle loading the base64 image
      setIsFullScreenOpen(true);
    } catch (error) {
      console.error('Error opening full screen:', error);
    } finally {
      setIsLoadingOriginal(false);
    }
  }, [isEditing]);

  const handleSaveCaption = useCallback(
    (caption: string) => {
      // Use local caption change if available, otherwise use the old API
      if (onCaptionChange) {
        onCaptionChange(photoId, caption);
      } else if (onUpdateCaption) {
        onUpdateCaption(photoId, caption);
      }
      setIsEditing(false);
    },
    [photoId, onCaptionChange, onUpdateCaption],
  );

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleDeleteClick = useCallback(async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await onDelete(photoId);
    } catch (error) {
      console.error('Error deleting photo:', error);
    } finally {
      setIsDeleting(false);
    }
  }, [isDeleting, onDelete, photoId]);

  const handleCloseFullScreen = useCallback(() => {
    setIsFullScreenOpen(false);
  }, []);

  return {
    // State
    isEditing,
    isDeleting,
    isFullScreenOpen,
    isLoadingOriginal,

    // Handlers
    handleEditClick,
    handlePhotoClick,
    handleSaveCaption,
    handleCancelEdit,
    handleDeleteClick,
    handleCloseFullScreen,
  };
};
